import React, { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import DropdownMC from '../../../components/DropdownMC';
import InputMC from '../../../components/InputMC';
import ButtonMC from '../../../components/ButtonMC';

const WebhookAuthSection = ({ methods, t }) => {
  const { control } = methods;
  const authType = useWatch({
    control,
    name: 'auth.type',
    defaultValue: 'none'
  });

  // Estado local para el asistente seleccionado y la región de Zoho
  const [oauthProvider, setOauthProvider] = React.useState('manual');
  const [zohoRegion, setZohoRegion] = React.useState('com');

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      // Escuchamos cualquier éxito de OAuth (Zoho o futuros)
      if (['ZOHO_OAUTH_SUCCESS', 'OAUTH_SUCCESS'].includes(event.data?.type)) {
        const { payload } = event.data;
        methods.setValue('auth.config.clientId', payload.client_id);
        methods.setValue('auth.config.clientSecret', payload.client_secret);
        methods.setValue('auth.config.refreshToken', payload.refresh_token);
        methods.setValue('auth.config.tokenUrl', payload.token_url);

        if (window.showtoast) window.showtoast(t('auth_success_toast'));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [methods, t]);

  const authTypeOptions = [
    { id: 'none', value: 'none', label: t('auth_type_none') },
    { id: 'apikey', value: 'apikey', label: t('auth_type_apikey') },
    { id: 'oauth2', value: 'oauth2', label: t('auth_type_oauth2') },
  ];

  const oauthProviders = [
    { id: 'manual', value: 'manual', label: t('oauth_provider_manual') },
    { id: 'zoho', value: 'zoho', label: 'Zoho CRM' },
  ];

  const zohoRegions = [
    { id: 'com', value: 'com', label: 'Zoho (.com)' },
    { id: 'eu', value: 'eu', label: 'Zoho (.eu)' },
    { id: 'in', value: 'in', label: 'Zoho (.in)' },
    { id: 'com.au', value: 'com.au', label: 'Zoho (.com.au)' },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
      <div className="tw-flex tw-flex-col">
        <h4 className="tw-text-sm tw-font-semibold tw-text-gray-950 tw-mb-0">
          {t('auth_title')}
        </h4>
        <p className="tw-text-xs tw-text-gray-700 tw-mb-0">
          {t('auth_description')}
        </p>
      </div>

      <DropdownMC
        id="auth_type"
        name="auth.type"
        label={t('auth_type_label')}
        placeholder={t('auth_type_placeholder')}
        options={authTypeOptions}
        defaultValue={authType}
        useFloatingLabel
      />

      {authType === 'apikey' && (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200">
          <InputMC
            name="auth.config.headerName"
            label={t('auth_header_name_label')}
            secondaryLabel={t('auth_header_name_floating_label')}
            placeholder="X-API-Key"
            useFloatingLabel
          />
          <InputMC
            name="auth.config.apiKey"
            label={t('auth_api_key_label')}
            secondaryLabel={t('auth_api_key_floating_label')}
            placeholder={t('auth_api_key_placeholder')}
            useFloatingLabel
            type="password"
            showPasswordToggle
          />
        </div>
      )}

      {authType === 'oauth2' && (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200">
          <div className="tw-flex tw-flex-col tw-gap-3">
            <DropdownMC
              id="oauth_assistant"
              name="oauth_assistant"
              label={t('oauth_assistant_label')}
              placeholder={t('select_provider')}
              options={oauthProviders}
              defaultValue={oauthProvider}
              onSelect={(val) => setOauthProvider(val)}
              useFloatingLabel
            />

            {oauthProvider === 'zoho' && (
              <div className="tw-flex tw-flex-col tw-gap-2 tw-p-3 tw-bg-blue-50 tw-rounded-lg tw-border tw-border-blue-100">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-flex-1">
                    <DropdownMC
                      id="zoho_region"
                      name="zoho_region"
                      options={zohoRegions}
                      defaultValue={zohoRegion}
                      onSelect={(val) => setZohoRegion(val)}
                      className="tw-mb-0"
                    />
                  </div>
                  <ButtonMC
                    text={t('zoho_connect_button')}
                    variant="PRIMARY"
                    onClick={() => {
                      const width = 600;
                      const height = 700;
                      const left = window.screen.width / 2 - width / 2;
                      const top = window.screen.height / 2 - height / 2;
                      window.open(`/webhooks/zoho_oauth/authorize?location=${zohoRegion}`, 'Zoho Auth', `width=${width},height=${height},top=${top},left=${left}`);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-2">
            <InputMC
              name="auth.config.clientId"
              label={t('auth_client_id_label')}
              placeholder={t('auth_client_id_placeholder')}
              useFloatingLabel
            />
            <InputMC
              name="auth.config.clientSecret"
              label={t('auth_client_secret_label')}
              placeholder={t('auth_client_secret_placeholder')}
              useFloatingLabel
              type="password"
              showPasswordToggle
            />
            <InputMC
              name="auth.config.refreshToken"
              label={t('auth_refresh_token_label')}
              placeholder={t('auth_refresh_token_placeholder')}
              useFloatingLabel
              type="password"
              showPasswordToggle
            />
            <InputMC
              name="auth.config.tokenUrl"
              label={t('auth_token_url_label')}
              placeholder={t('auth_token_url_placeholder')}
              useFloatingLabel
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookAuthSection;
